import React from 'react'
import { Icon } from '../../../../../atoms/Icon'
import { InfoTooltip } from '../../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../../TooltipMetricExplanation'
import {
  Grid,
  Card,
  CardHeader,
  CardTitle,
  MetricGrid,
  MetricRow,
  MetricLabel,
  MetricValue,
} from '../../../BuildingCharacteristics.styles'
import type { NeighborhoodStatisticsGridProps } from '../NeighborhoodContextTab.types'

export const NeighborhoodStatisticsGrid: React.FC<
  NeighborhoodStatisticsGridProps
> = ({ neighborhoodAnalysis, t }) => {
  return (
    <Grid $columns={3}>
      <Card>
        <CardHeader>
          <CardTitle>
            <Icon name="trending-up" size="small" />
            {t('neighborhood.statistics.height.title')}
          </CardTitle>
        </CardHeader>
        <MetricGrid>
          <MetricRow>
            <MetricLabel>
              {t('neighborhood.statistics.mean')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('neighborhood.statistics.height.mean.tooltip.title')}
                  shortDescription={t(
                    'neighborhood.statistics.height.mean.tooltip.description'
                  )}
                  calculation={t(
                    'neighborhood.statistics.height.mean.tooltip.calculation'
                  )}
                  importance={t(
                    'neighborhood.statistics.height.mean.tooltip.importance'
                  )}
                  source={t('neighborhood.statistics.height.mean.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {neighborhoodAnalysis.statistics.height.mean.toFixed(1)} m
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('neighborhood.statistics.median')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'neighborhood.statistics.height.median.tooltip.title'
                  )}
                  shortDescription={t(
                    'neighborhood.statistics.height.median.tooltip.description'
                  )}
                  calculation={t(
                    'neighborhood.statistics.height.median.tooltip.calculation'
                  )}
                  importance={t(
                    'neighborhood.statistics.height.median.tooltip.importance'
                  )}
                  source={t('neighborhood.statistics.height.median.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {neighborhoodAnalysis.statistics.height.median.toFixed(1)} m
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('neighborhood.statistics.range')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'neighborhood.statistics.height.range.tooltip.title'
                  )}
                  shortDescription={t(
                    'neighborhood.statistics.height.range.tooltip.description'
                  )}
                  calculation={t(
                    'neighborhood.statistics.height.range.tooltip.calculation'
                  )}
                  importance={t(
                    'neighborhood.statistics.height.range.tooltip.importance'
                  )}
                  source={t('neighborhood.statistics.height.range.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {neighborhoodAnalysis.statistics.height.min.toFixed(1)} -{' '}
              {neighborhoodAnalysis.statistics.height.max.toFixed(1)} m
            </MetricValue>
          </MetricRow>
        </MetricGrid>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <Icon name="square" size="small" />
            {t('neighborhood.statistics.area.title')}
          </CardTitle>
        </CardHeader>
        <MetricGrid>
          <MetricRow>
            <MetricLabel>
              {t('neighborhood.statistics.mean')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('neighborhood.statistics.area.mean.tooltip.title')}
                  shortDescription={t(
                    'neighborhood.statistics.area.mean.tooltip.description'
                  )}
                  calculation={t(
                    'neighborhood.statistics.area.mean.tooltip.calculation'
                  )}
                  importance={t(
                    'neighborhood.statistics.area.mean.tooltip.importance'
                  )}
                  source={t('neighborhood.statistics.area.mean.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {Math.round(neighborhoodAnalysis.statistics.area.mean)} m²
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('neighborhood.statistics.median')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('neighborhood.statistics.area.median.tooltip.title')}
                  shortDescription={t(
                    'neighborhood.statistics.area.median.tooltip.description'
                  )}
                  calculation={t(
                    'neighborhood.statistics.area.median.tooltip.calculation'
                  )}
                  importance={t(
                    'neighborhood.statistics.area.median.tooltip.importance'
                  )}
                  source={t('neighborhood.statistics.area.median.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {Math.round(neighborhoodAnalysis.statistics.area.median)} m²
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('neighborhood.statistics.range')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('neighborhood.statistics.area.range.tooltip.title')}
                  shortDescription={t(
                    'neighborhood.statistics.area.range.tooltip.description'
                  )}
                  calculation={t(
                    'neighborhood.statistics.area.range.tooltip.calculation'
                  )}
                  importance={t(
                    'neighborhood.statistics.area.range.tooltip.importance'
                  )}
                  source={t('neighborhood.statistics.area.range.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {Math.round(neighborhoodAnalysis.statistics.area.min)} -{' '}
              {Math.round(neighborhoodAnalysis.statistics.area.max)} m²
            </MetricValue>
          </MetricRow>
        </MetricGrid>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <Icon name="layers" size="small" />
            {t('neighborhood.statistics.floor.title')}
          </CardTitle>
        </CardHeader>
        <MetricGrid>
          <MetricRow>
            <MetricLabel>
              {t('neighborhood.statistics.mean')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('neighborhood.statistics.floor.mean.tooltip.title')}
                  shortDescription={t(
                    'neighborhood.statistics.floor.mean.tooltip.description'
                  )}
                  calculation={t(
                    'neighborhood.statistics.floor.mean.tooltip.calculation'
                  )}
                  importance={t(
                    'neighborhood.statistics.floor.mean.tooltip.importance'
                  )}
                  source={t('neighborhood.statistics.floor.mean.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {t('neighborhood.statistics.floor.floorsValue', {
                floors:
                  neighborhoodAnalysis.statistics.floor_count.mean.toFixed(1),
              })}
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('neighborhood.statistics.median')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'neighborhood.statistics.floor.median.tooltip.title'
                  )}
                  shortDescription={t(
                    'neighborhood.statistics.floor.median.tooltip.description'
                  )}
                  calculation={t(
                    'neighborhood.statistics.floor.median.tooltip.calculation'
                  )}
                  importance={t(
                    'neighborhood.statistics.floor.median.tooltip.importance'
                  )}
                  source={t('neighborhood.statistics.floor.median.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {t('neighborhood.statistics.floor.floorsValue', {
                floors: Math.round(
                  neighborhoodAnalysis.statistics.floor_count.median
                ),
              })}
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('neighborhood.statistics.range')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('neighborhood.statistics.floor.range.tooltip.title')}
                  shortDescription={t(
                    'neighborhood.statistics.floor.range.tooltip.description'
                  )}
                  calculation={t(
                    'neighborhood.statistics.floor.range.tooltip.calculation'
                  )}
                  importance={t(
                    'neighborhood.statistics.floor.range.tooltip.importance'
                  )}
                  source={t('neighborhood.statistics.floor.range.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {t('neighborhood.statistics.floor.floorsRange', {
                min: Math.round(
                  neighborhoodAnalysis.statistics.floor_count.min
                ),
                max: Math.round(
                  neighborhoodAnalysis.statistics.floor_count.max
                ),
              })}
            </MetricValue>
          </MetricRow>
        </MetricGrid>
      </Card>
    </Grid>
  )
}
